'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Wrench, CalendarX, User } from 'lucide-react';
import BlurImage from '@/components/BlurImage';
import LightboxGallery from '@/components/LightboxGallery';
import { ButtonSpinner } from '@/components/ButtonSpinner';
import { AmenityIcon } from '@/components/AmenityIcon';
import ArrowIcon from '@/components/ArrowIcon';
import type { CmsRoom } from '@/lib/cms';
import siteTheme from '@/lib/theme';
import { buildSignedBookingUrl } from '@/lib/booking-navigate';
import { useRoomSelection } from '@/lib/room-selection-context';
function toISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const { fonts } = siteTheme;

type RoomStatus = 'available' | 'fully_booked' | 'maintenance' | 'not_setup';

export default function RoomCard({ room, status, checkin: checkinProp, checkout: checkoutProp }: { room: CmsRoom; status?: RoomStatus; checkin?: string; checkout?: string }) {
  const selection     = useRoomSelection();
  const inSelection   = !!selection?.state.active;
  // availableIds contains Sandy roomTypeIds — must match via external_room_type_id, not Strapi room.id
  const isSelAvail    = inSelection && room.external_room_type_id != null && selection!.state.availableIds.has(room.external_room_type_id);
  const isSelected    = inSelection && selection!.state.selected.includes(room.slug ?? String(room.id));

  const bypassAvailability = process.env.isByPassAvailablity === 'true' || process.env.NEXT_PUBLIC_IS_BYPASS_AVAILABLITY === 'true';
  // In selection mode, override availability from context's fresh check
  const isAvailable   = bypassAvailability ? true : (inSelection ? isSelAvail : (!status || status === 'available'));
  const isMaintenance = bypassAvailability ? false : (!inSelection && status === 'maintenance');
  const isUnavailable = !isAvailable;

  const router = useRouter();
  const images = room.gallery?.length
    ? room.gallery.map((g) => ({ url: g.url, alt: g.alternativeText || room.name }))
    : [];

  const [current,     setCurrent]     = useState(0);
  const [hovered,     setHovered]     = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [booking,     setBooking]     = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);



  async function handleBookNow() {
    router.push('?contact-booking=true', { scroll: false });
    /*
    setBooking(true);
    const checkin  = checkinProp  ?? toISO(new Date());
    const checkout = checkoutProp ?? toISO(new Date(Date.now() + 86_400_000));
    const slug     = room.slug || String(room.id);
    try {
      const url = await buildSignedBookingUrl({ type: slug, checkin, checkout, rooms: 1, adults: 1 });
      router.push(url);
    } catch {
      router.push(`/book?checkin=${checkin}&checkout=${checkout}&rooms=1&adults=1`);
    }
    // booking stays true — button keeps showing "Navigating to booking page…"
    // until the booking page mounts and this card unmounts
    */
  }

  useEffect(() => {
    if (hovered && images.length > 1) {
      intervalRef.current = setInterval(() => setCurrent(c => (c + 1) % images.length), 1800);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [hovered, images.length]);

  const allAmenities = room.amenities ?? [];
  const amenityRef      = useRef<HTMLDivElement>(null);
  const [amenityShowCount, setAmenityShowCount] = useState<number | null>(null);
  const [measureTrigger, setMeasureTrigger]     = useState(0);

  useEffect(() => {
    const el = amenityRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setAmenityShowCount(null);
      setMeasureTrigger(t => t + 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (amenityShowCount !== null) return;
    const el = amenityRef.current;
    if (!el || allAmenities.length === 0) return;

    const items = el.querySelectorAll<HTMLElement>('[data-chip]');
    if (items.length === 0) return;

    const rowTops = new Set<number>();
    items.forEach(item => rowTops.add(item.offsetTop));
    const sorted = [...rowTops].sort((a, b) => a - b);

    if (sorted.length <= 2) return;

    const maxTop  = sorted[1];
    const fitting = [...items].filter(item => item.offsetTop <= maxTop).length;
    setAmenityShowCount(Math.max(1, fitting - 1));
  }, [amenityShowCount, allAmenities.length, measureTrigger]);

  const visibleAmenities = amenityShowCount !== null ? allAmenities.slice(0, amenityShowCount) : allAmenities;
  const extraCount       = allAmenities.length - visibleAmenities.length;

  const handleCardClick = inSelection && isSelAvail
    ? () => selection!.toggle(room.slug ?? String(room.id))
    : undefined;

  return (
    <>
      <div
        onClick={handleCardClick}
        className={`group relative overflow-hidden rounded-[14px] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 ${
          inSelection && isSelAvail
            ? isSelected
              ? 'border-[2px] border-[#b5935b] cursor-pointer shadow-[0_0_0_3px_rgba(181,147,91,0.25)]'
              : 'border border-[#e7dfd2] cursor-pointer hover:border-[#b5935b] hover:shadow-[0_8px_36px_rgba(181,147,91,0.15)]'
            : isUnavailable
              ? 'border border-[#e7dfd2] opacity-[0.92]'
              : 'border border-[#e7dfd2] hover:shadow-[0_8px_36px_rgba(181,147,91,0.22),0_0_0_1.5px_rgba(181,147,91,0.18)]'
        }`}
        onMouseEnter={() => { if (!inSelection) setHovered(true); }}
        onMouseLeave={() => { if (!inSelection) setHovered(false); }}
      >

        {/* ── Image ── */}
        <div className="relative h-[270px] overflow-hidden bg-[#f3efe8]">

          {images.length > 0 ? images.map((img, i) => (
            <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === current ? 1 : 0, transition: 'opacity 0.6s ease', zIndex: 1 }}>
              <BlurImage
                src={img.url} alt={img.alt} fill
                className={`object-cover transition duration-700 ${
                  isUnavailable ? 'grayscale' : 'group-hover:scale-[1.02]'
                }`}
                sizes="(max-width: 600px) 100vw, (max-width: 1024px) 50vw, 34vw"
                priority={i === 0}
              />
            </div>
          )) : (
            <div className="absolute inset-0 z-[1] flex items-center justify-center">
              <span style={{ fontFamily: fonts.sans, fontSize: '1.1rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.2)' }}>
                No image
              </span>
            </div>
          )}

          {/* Soft bottom gradient — always present */}
          <div className="absolute inset-0 z-[2] bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />

          {/* Lightbox tap zone — hidden in selection mode */}
          {images.length > 0 && !inSelection && (
            <button
              type="button"
              onClick={e => { e.preventDefault(); e.stopPropagation(); setLightboxIdx(current); }}
              aria-label="View gallery"
              className="absolute inset-0 z-[3] cursor-zoom-in border-none bg-transparent p-0"
            />
          )}

          {/* Selection checkmark badge — top-right when selected */}
          {inSelection && isSelAvail && isSelected && (
            <div className="absolute right-4 top-4 z-[6] flex h-9 w-9 items-center justify-center rounded-full shadow-md"
              style={{ background: '#b5935b' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8l3.5 3.5L13 5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}

          {/* ── Sold out / maintenance overlay ── */}
          {isUnavailable && (
            <div className="absolute inset-0 z-[4] backdrop-blur-[1px]" style={{ background: 'rgba(13,13,13,0.55)' }}>
              <div className="flex h-full flex-col items-center justify-center px-8 text-center text-white">

                {/* Status badge — inside overlay */}
                <div
                  className="mb-5 rounded-full border border-white/15 bg-white/10 px-5 py-[10px] text-[10px] font-medium uppercase tracking-[0.24em]"
                  style={{ fontFamily: fonts.sans }}
                >
                  {isMaintenance ? 'Maintenance' : 'Sold Out'}
                </div>

                {/* Status icon */}
                <div className="mb-5">
                  {isMaintenance
                    ? <Wrench size={56} strokeWidth={1.5} />
                    : <CalendarX size={56} strokeWidth={1.5} />
                  }
                </div>

                <h3
                  className="text-[20px] font-light uppercase tracking-[0.18em] text-white"
                  style={{ fontFamily: fonts.serif }}
                >
                  {isMaintenance ? 'Under Maintenance' : 'Room Occupied'}
                </h3>

                <div className="my-5 h-px w-14 bg-[#b5935b]" />

                <p
                  className="max-w-[240px] text-[13px] leading-7 text-white/75"
                  style={{ fontFamily: fonts.sans }}
                >
                  {isMaintenance
                    ? 'This room is temporarily under maintenance.'
                    : 'This room is currently booked for the selected dates.'}
                </p>
              </div>
            </div>
          )}

          {/* Featured badge — top-left, available only */}
          {isAvailable && room.featured && (
            <div
              className="absolute left-4 top-4 z-[5] rounded-full px-4 py-[9px] text-[10px] font-semibold uppercase tracking-[0.22em] text-white shadow-sm"
              style={{ fontFamily: fonts.sans, background: '#b5935b' }}
            >
              Featured
            </div>
          )}

          {/* PAX pill — bottom-right, white background, always visible */}
          <div className="absolute bottom-4 right-4 z-[5] rounded-full bg-white/85 px-3 py-[7px] shadow-[0_4px_12px_rgba(0,0,0,0.12)] backdrop-blur-md">
            <div className="flex items-center gap-[7px] text-[11px] font-medium text-[#707070]" style={{ fontFamily: fonts.sans }}>
              <User size={14} strokeWidth={1.8} />
              {room.max_guests} Pax
            </div>
          </div>

          {/* Image dots */}
          {images.length > 1 && (
            <div
              className="absolute bottom-4 left-4 z-[5] flex gap-[5px]"
              onClick={e => e.stopPropagation()}
            >
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={e => { e.stopPropagation(); setCurrent(i); }}
                  aria-label={`Image ${i + 1}`}
                  style={{
                    width: i === current ? '1.4rem' : '0.4rem', height: '0.4rem',
                    borderRadius: '3px',
                    backgroundColor: i === current ? '#fff' : 'rgba(255,255,255,0.5)',
                    border: 'none', padding: 0, cursor: 'pointer',
                    transition: 'width 0.3s ease, background-color 0.3s ease',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Content ── */}
        <div className="p-6">

          {/* Room name */}
          <h2
            className="text-[26px] leading-none tracking-[-0.02em]"
            style={{ fontFamily: fonts.serif, color: isUnavailable ? 'rgba(0,0,0,0.4)' : '#17384d' }}
          >
            {room.name}
          </h2>

          {/* Description */}
          <p
            className="mt-4 line-clamp-2 text-[14px] leading-7"
            style={{ fontFamily: fonts.sans, color: isUnavailable ? 'rgba(0,0,0,0.32)' : '#787878' }}
          >
            {room.description || 'A beautifully appointed room designed for your comfort and relaxation.'}
          </p>

          {/* Amenity chips — 2-row flex, overflow → "+ N more" */}
          {allAmenities.length > 0 && (
            <div ref={amenityRef} className="mt-4 flex flex-wrap gap-[6px]">
              {visibleAmenities.map(a => (
                <div
                  key={a.id}
                  data-chip
                  className="flex items-center gap-[5px] rounded-full px-[10px] py-[5px] text-[10px] font-medium leading-none"
                  style={{
                    fontFamily: fonts.sans,
                    background: isUnavailable ? 'rgba(0,0,0,0.04)' : '#f5f0ea',
                    color: isUnavailable ? 'rgba(0,0,0,0.28)' : '#9b7442',
                  }}
                >
                  <span style={{ color: isUnavailable ? 'rgba(0,0,0,0.18)' : '#b5935b', lineHeight: 0 }}>
                    {a.image?.url ? (
                      <Image
                        src={a.image.url} alt={a.label}
                        width={12} height={12}
                        style={{ width: '0.75rem', height: '0.75rem', objectFit: 'contain', opacity: isUnavailable ? 0.35 : 0.85 }}
                      />
                    ) : (
                      <AmenityIcon label={a.label} icon={a.icon} size={12} />
                    )}
                  </span>
                  {a.label}
                </div>
              ))}
              {extraCount > 0 && (
                <div
                  className="rounded-full px-[10px] py-[5px] text-[10px] font-medium leading-none"
                  style={{
                    fontFamily: fonts.sans,
                    background: isUnavailable ? 'rgba(0,0,0,0.04)' : '#f5f0ea',
                    color: isUnavailable ? 'rgba(0,0,0,0.28)' : '#9b7442',
                  }}
                >
                  +{extraCount} more
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="my-6 h-px bg-[#eee7dd]" />

          {/* Price */}
          <div>
            <p
              className="mb-2 text-[11px] font-semibold uppercase tracking-[0.24em]"
              style={{ fontFamily: fonts.sans, color: isUnavailable ? 'rgba(0,0,0,0.3)' : '#a98758' }}
            >
              From
            </p>
            <div className="flex items-end gap-2">
              <span
                className="text-[44px] leading-none tracking-[-0.04em]"
                style={{ fontFamily: fonts.serif, color: isUnavailable ? 'rgba(0,0,0,0.32)' : '#0d2f44' }}
                
              >
                ₹{room.base_price.toLocaleString('en-IN')}
              </span>
              <span
                className="mb-[6px] text-[15px]"
                style={{ fontFamily: fonts.sans, color: isUnavailable ? 'rgba(0,0,0,0.32)' : '#767676' }}
              >
                / night
              </span>
            </div>
          </div>

          {/* CTAs */}
          {inSelection && isSelAvail ? (
            /* Selection mode: single full-width toggle button */
            <div className="mt-6">
              <button
                type="button"
                onClick={e => { e.stopPropagation(); selection!.toggle(room.slug ?? String(room.id)); }}
                className="flex h-[50px] w-full items-center justify-center gap-[8px] rounded-[0.5rem] text-[11px] font-semibold uppercase tracking-[0.18em] transition-all"
                style={{
                  fontFamily: fonts.sans,
                  background: isSelected ? '#b5935b' : 'transparent',
                  border: `1.5px solid ${isSelected ? '#b5935b' : '#d8c8b1'}`,
                  color: isSelected ? '#fff' : '#9f8451',
                  cursor: 'pointer',
                }}
              >
                {isSelected ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Selected
                  </>
                ) : (
                  <>
                    Select Room
                    <ArrowIcon variant="short" size={13} />
                  </>
                )}
              </button>
            </div>
          ) : (
            /* Normal mode: 2-column CTAs */
            <div className="mt-6 grid grid-cols-2 gap-3">

              {/* View Details — hidden while navigating so the Book button can span full width */}
              {!booking && (
                <Link
                  href={`/rooms/${room.slug || room.id}`}
                  className="rc-btn-outline flex h-[50px] items-center justify-center gap-[6px] rounded-[0.5rem] border text-[11px] font-semibold uppercase tracking-[0.18em] transition-all"
                  style={{
                    fontFamily: fonts.sans,
                    borderColor: isUnavailable ? 'rgba(0,0,0,0.1)' : '#d8c8b1',
                    background: isUnavailable ? 'rgba(0,0,0,0.05)' : '#fff',
                    color: isUnavailable ? 'rgba(0,0,0,0.3)' : '#16384c',
                  }}
                >
                  View Details
                  {isAvailable && (
                    <ArrowIcon variant="short" size={13} />
                  )}
                </Link>
              )}

              {/* Book Now / Not Available */}
              {isAvailable ? (
                <button
                  type="button"
                  onClick={handleBookNow}
                  disabled={booking}
                  className={`rc-btn-book flex h-[50px] items-center justify-center gap-[6px] rounded-[0.5rem] text-[11px] font-semibold uppercase tracking-[0.18em] transition-all ${booking ? 'col-span-2' : ''}`}
                  style={{
                    fontFamily: fonts.sans,
                    background: '#9f8451',
                    color: '#fff',
                    opacity: booking ? 0.85 : 1,
                    cursor: booking ? 'wait' : 'pointer',
                  }}
                  onMouseEnter={e => { if (!booking) (e.currentTarget as HTMLElement).style.background = '#8d7446'; }}
                  onMouseLeave={e => { if (!booking) (e.currentTarget as HTMLElement).style.background = '#9f8451'; }}
                >
                  {booking ? (
                    <><ButtonSpinner color="#fff" size={13} /> Navigating to booking page…</>
                  ) : (
                    <>
                      Book Now
                      <ArrowIcon variant="short" size={13} />
                    </>
                  )}
                </button>
              ) : (
                <button
                  disabled
                  className="h-[50px] cursor-not-allowed rounded-[0.5rem] text-[11px] font-semibold uppercase tracking-[0.18em]"
                  style={{ fontFamily: fonts.sans, background: '#d8d5d2', color: 'rgba(255,255,255,0.7)' }}
                >
                  {isMaintenance ? 'Maintenance' : 'Not Available'}
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      {lightboxIdx !== null && (
        <LightboxGallery images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </>
  );
}
